import {
  ArrowTopRightOnSquareIcon,
  DocumentArrowDownIcon,
  ListBulletIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/react/16/solid';
import { SparklesIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagsContext } from '../../../../../../../../providers/FeatureFlagsProvider/FeatureFlags.context';
import { useModalsContext } from '../../../../../../../../providers/ModalsProvider/Modals.context';
import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums';
import { useUserContext } from '../../../../../../../../providers/UserProvider/User.context';
import { MessageState } from '../../../../../../Chat.enum';
import { useChatContext } from '../../../../../../Chat.hook';
import {
  tChatEvent,
  tInteractiveButton,
  tInteractiveHeader,
  tInteractiveOutbound,
} from '../../../../../../Chat.types';
import { safeInteractiveUrl } from '../../../../../MessageBar/components/InteractiveAction/InteractiveAction.functions';
import useMessageBubbleMenu from '../MessageBubbleMenu/MessageBubbleMenu.hook';
import {
  integrationBubbleTitleKey,
} from '../MessageBubble/MessageBubbleV2.functions';
import MessageSources from 'src/components/MessageSources/MessageSources.component';
import './OutboundInteractiveBubble.styles.scss';
import useBubbleDate from '../../../../bubbleDate/useBubbleDate.hook';

type tOutboundInteractiveBubbleProps = {
  event: tChatEvent;
  interactive: tInteractiveOutbound;
};

// Renders outbound Reply Buttons / CTA URL / List messages. Reuses
// `MessageBubbleV2` container + header classes so that username, timestamp,
// delivery checkmarks, and the top-right tail match the rest of the outbound
// chat stream. Interactive-specific bits (body, footer, tappable action
// rows) live in this component's own BEM block. The generic MessageBubbleV2
// only renders `content.text`, so without this we'd drop the buttons
// entirely and sellers had no way to verify the template rendered correctly
// on the recipient side.
//
// Layout rules mirror WhatsApp Web:
//   1 button  → single full-width row
//   2 buttons → two side-by-side cells divided by a vertical line
//   3 buttons → three stacked full-width rows
//   cta_url   → single row with an external-link icon (opens in new tab
//               because the agent legitimately can click it to verify URL)
//   list      → single row with list-bullet icon (static — the bottom-sheet
//               behavior only runs on the recipient device)
//
// No reply icon on the button rows: WhatsApp shows that hint only on the
// recipient side; sellers don't need it.
export default function OutboundInteractiveBubble(
  props: tOutboundInteractiveBubbleProps,
) {
  const { event, interactive } = props;
  const { message, createdAt, createdBy } = event;

  const { showCheckStatus } = useMessageBubbleMenu();
  const { selectedChat } = useChatContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { currentUser } = useUserContext();
  const { isSmartLeadBotEnabled } = useFeatureFlagsContext();
  const { t } = useTranslation('chat/chat');
  const bubbleDate = useBubbleDate();

  const footer = interactive.footer?.trim();
  const body = interactive.body?.trim() || event.content?.text || '';

  const isSLBMessage = isSmartLeadBotEnabled && createdBy === 'smart_leadbot';
  const isCurrentUser = createdBy === currentUser?.authId;
  const messageTitle =
    (isSLBMessage && 'Lead Agent') ||
    (createdBy === 'leadbot' && 'Leadbot') ||
    (createdBy === 'Follow Up' && 'Leadsales') ||
    (isCurrentUser && t('messageBubble.you')) ||
    (createdBy &&
      selectedChat?.lead?.users?.find((user) => user.auth_id === createdBy)
        ?.name) ||
    t(
      integrationBubbleTitleKey[
        selectedLeadForChatModal?.chat?.integration || eChannelType.WHATSAPP_WEB
      ],
    );

  return (
    <div className="MessageBubbleV2 OutboundInteractiveBubble">
      <div className="MessageBubbleV2__Wrapper col-span-12 chat-end justify-end">
        <div className="group flex items-end gap-1 flex-row-reverse">
          <div
            className={classNames(
              'MessageBubbleV2__Container MessageBubbleV2__Container--tail-end OutboundInteractiveBubble__Container',
              {
                'MessageBubbleV2__Container--outgoing': !isSLBMessage,
                'MessageBubbleV2__Container--slb': isSLBMessage,
              },
            )}
          >
            <div className="MessageBubbleV2__Header justify-start">
              {isSLBMessage ? (
                <div className="flex items-center gap-1 min-w-36">
                  <span className="inline-flex items-center gap-1 px-2 py-0 rounded-full text-[10px] font-normal text-violet-600 bg-violet-100 border border-white shadow-sm">
                    <SparklesIcon className="w-3 h-3" />
                    Lead Agent
                  </span>
                </div>
              ) : (
                <div className="text-xs min-w-36 MessageBubbleV2__accent">
                  {messageTitle}
                </div>
              )}

              <div className="MessageBubbleV2__Header__Date flex w-full gap-x-2 items-center MessageBubbleV2__accent justify-end">
                <span>{bubbleDate.stamp(createdAt)}</span>
              </div>

              {showCheckStatus(
                message?.state ?? MessageState.SENT,
                message?.error,
              )}
            </div>

            <div className="OutboundInteractiveBubble__Body">
              {interactive.header ? (
                <InteractiveMediaHeader header={interactive.header} />
              ) : null}

              {body ? (
                <p className="OutboundInteractiveBubble__Body__Text">{body}</p>
              ) : null}

              {footer ? (
                <p className="OutboundInteractiveBubble__Body__Footer">
                  {footer}
                </p>
              ) : null}

              {/*
                AGI-272: the same Lead Agent evidence chip MessageBubbleV2
                mounts. EventBubble routes an interactive reply here and returns
                before the generic MESSAGE case runs, so wiring the control only
                there hid the sources of every Lead Agent turn that carried
                buttons. Inside __Body because the container's padding is zeroed
                to let the action rows hug the bubble wall — __Body is what
                holds the message text's left edge, so the annotation lines up
                with the text it annotates. Gated on isSLBMessage like the badge
                above; an event with no sources renders nothing either way
                (MessageSources' own render floor).
              */}
              {isSLBMessage && <MessageSources sources={event.sources} />}
            </div>

            <InteractiveActions interactive={interactive} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Renders the interactive header inside the padded body area so image /
// video / document media match the normal image-message look (fixed
// `w-72` width, `rounded-md`, vertical centering) instead of filling the
// bubble edge-to-edge. Text headers render as a bolded title line above
// the body text.
function InteractiveMediaHeader({ header }: { header: tInteractiveHeader }) {
  if (header.type === 'text') {
    const text = header.text?.trim();
    if (!text) return null;
    return <p className="OutboundInteractiveBubble__Body__Title">{text}</p>;
  }
  if (header.type === 'image') {
    const safeLink = safeInteractiveUrl(header.image?.link);
    if (safeLink) {
      return (
        <img
          src={safeLink}
          alt=""
          className="OutboundInteractiveBubble__HeaderImage"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      );
    }
    return (
      <div className="OutboundInteractiveBubble__HeaderMediaPlaceholder">
        <PhotoIcon className="w-5 h-5" />
        <span>Imagen</span>
      </div>
    );
  }
  if (header.type === 'video') {
    return (
      <div className="OutboundInteractiveBubble__HeaderMediaPlaceholder">
        <VideoCameraIcon className="w-5 h-5" />
        <span>Video</span>
      </div>
    );
  }
  if (header.type === 'document') {
    const safeHref = safeInteractiveUrl(header.document?.link);
    // Prefer the human-facing filename the sender uploaded. When the
    // interactive payload pre-dates the filename threading or the uploader
    // didn't capture it, fall back to the URL's last path segment so the
    // bubble still reads as a file card instead of spilling the raw URL
    // into the chat.
    const label =
      header.document?.filename?.trim() ||
      safeHref?.split('/').pop()?.split('?')[0] ||
      'Documento';
    if (!safeHref) {
      return (
        <div className="OutboundInteractiveBubble__HeaderMediaPlaceholder">
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{label}</span>
        </div>
      );
    }
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        referrerPolicy="no-referrer"
        className="OutboundInteractiveBubble__HeaderDocument"
      >
        <DocumentArrowDownIcon className="OutboundInteractiveBubble__HeaderDocument__Icon" />
        <span className="OutboundInteractiveBubble__HeaderDocument__Label">
          {label}
        </span>
      </a>
    );
  }
  return null;
}

function InteractiveActions({
  interactive,
}: {
  interactive: tInteractiveOutbound;
}) {
  if (interactive.type === 'button') {
    const buttons = interactive.action.buttons.filter(
      (b: tInteractiveButton) => b.title.trim() !== '',
    );
    if (buttons.length === 0) return null;
    const layoutClass = classNames('OutboundInteractiveBubble__Actions', {
      'OutboundInteractiveBubble__Actions--stack': buttons.length !== 2,
      'OutboundInteractiveBubble__Actions--split': buttons.length === 2,
    });
    return (
      <div className={layoutClass}>
        {buttons.map((btn, idx) => (
          <div
            key={idx}
            className="OutboundInteractiveBubble__Action"
            title={btn.title}
          >
            <span className="OutboundInteractiveBubble__Action__Label">
              {btn.title}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (interactive.type === 'cta_url') {
    const label = interactive.action.display_text.trim();
    if (!label) return null;
    const safeHref = safeInteractiveUrl(interactive.action.url);
    return (
      <div className="OutboundInteractiveBubble__Actions OutboundInteractiveBubble__Actions--stack">
        {safeHref ? (
          <a
            className="OutboundInteractiveBubble__Action"
            href={safeHref}
            target="_blank"
            rel="noopener noreferrer"
            referrerPolicy="no-referrer"
            title={label}
          >
            <ArrowTopRightOnSquareIcon className="OutboundInteractiveBubble__Action__Icon" />
            <span className="OutboundInteractiveBubble__Action__Label">
              {label}
            </span>
          </a>
        ) : (
          <div
            className="OutboundInteractiveBubble__Action OutboundInteractiveBubble__Action--disabled"
            title={label}
            aria-disabled="true"
          >
            <ArrowTopRightOnSquareIcon className="OutboundInteractiveBubble__Action__Icon" />
            <span className="OutboundInteractiveBubble__Action__Label">
              {label}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (interactive.type === 'list') {
    const label = interactive.action.button_text.trim();
    if (!label) return null;
    return (
      <div className="OutboundInteractiveBubble__Actions OutboundInteractiveBubble__Actions--stack">
        <div className="OutboundInteractiveBubble__Action" title={label}>
          <ListBulletIcon className="OutboundInteractiveBubble__Action__Icon" />
          <span className="OutboundInteractiveBubble__Action__Label">
            {label}
          </span>
        </div>
      </div>
    );
  }

  return null;
}
